import React, { useState, memo } from "react";
import { Button, Grid, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import { map } from "lodash";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import useStableCallback from "../../hooks/useStableCallback";

const OrderFooterSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
};

const OrderFooterBtnSx = { mr: 2 };

const OrderFooterDropdownSx = {
  ...OrderFooterBtnSx,
  width: 135,
};

const OrderFooterInputSx = {
  ...OrderFooterBtnSx,
  "& .MuiFormHelperText-root": {
    position: "absolute",
    bottom: -20,
  },
};

const DatePikerProps = {
  sx: {
    color: (theme) => theme.palette.primary.main,
  },
};

const ResetBtnSx = {
  mr: 2,
};

const PONumberInputProps = {
  maxLength: 10,
};

const CustomDateTextField = styled(TextField)(() => ({
  ".MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "black",
  },
}));

export const GeneratePOByMap = [
  {
    id: 1,
    label: "Department",
  },
  {
    id: 2,
    label: "POG",
  },
  {
    id: 3,
    label: "Custom",
  },
];

const inputRegex = /^(?!.*[- ]{2})[A-Za-z0-9 ]*(?:[- ][A-Za-z0-9 ]*){0,4}$/is;
const invalidRegex = /^[ -0]*$/;
const startEndSpecialCharRegex =
  /^[a-zA-Z0-9]+[a-zA-Z0-9:.\- ]+[a-zA-Z0-9:.\- ][a-zA-Z0-9]+$/;
const singleCharRegex =
  /^(?!.*[-]{2})(?=.*[a-zA-Z0-9]$)[a-zA-Z0-9][a-zA-Z0-9-]*$/;

const isValidPONumber = (value, setError) => {
  const currentValue = value.trim();
  if (!!currentValue) {
    if (
      currentValue.length <= 3 &&
      singleCharRegex.test(currentValue) &&
      !["0", "00", "000"].includes(currentValue)
    ) {
      setError({ error: false, helperText: "" });
      return true;
    }
    if (
      !currentValue.length ||
      !inputRegex.test(currentValue) ||
      invalidRegex.test(currentValue) ||
      !startEndSpecialCharRegex.test(currentValue)
    ) {
      setError({ error: true, helperText: "Invalid PO Number" });
      return false;
    }
    setError({ error: false, helperText: "" });
    return true;
  }
  setError({ error: true, helperText: "Please enter valid PO Number" });
  return false;
};

const OrderFooterActions = ({
  onCreatePONumbers,
  onSaveDates,
  onDeleteDates,
  onReset,
  disableActions = false,
}) => {
  const [dateValue, setDateValue] = useState(null);
  const [generatePOBy, setGeneratePOBy] = useState(GeneratePOByMap[0].id);
  const [customPONumber, setCustomPONumber] = useState("");
  const [inputError, setInputError] = useState({
    error: false,
    helperText: "",
  });

  const handleDateChange = useStableCallback((newValue) => {
    setDateValue(newValue);
  });

  const handleCreatePONumbers = useStableCallback(() => {
    onCreatePONumbers({
      generatePOBy: GeneratePOByMap[generatePOBy - 1].label,
      customPONumber: !!customPONumber ? customPONumber : null,
    });
  });

  const handleSaveDates = useStableCallback(() => {
    const value = dateValue.startOf("date").format();
    const date = value.split("T")[0];
    onSaveDates(date);
  });

  const handleDeleteDates = useStableCallback(() => {
    onDeleteDates();
  });

  const handleReset = useStableCallback(() => {
    setGeneratePOBy(GeneratePOByMap[0].id);
    setInputError({ error: false, helperText: "" });
    setCustomPONumber("");
    setDateValue(null);
    onReset();
  });

  const dateInputRenderer = useStableCallback((params) => (
    <CustomDateTextField
      {...params}
      disabled
      size="small"
      InputLabelProps={{ shrink: true }}
      sx={{ mr: 2 }}
    />
  ));

  const handleGeneratePOByChange = useStableCallback((event) => {
    setInputError({ error: false, helperText: "" });
    setCustomPONumber("");
    setGeneratePOBy(event?.target?.value);
  });

  const handleCustomPONumberInput = useStableCallback((event) => {
    const value = event?.target?.value ?? "";
    const updatedValue = value.toUpperCase();
    isValidPONumber(updatedValue, setInputError);
    setCustomPONumber(updatedValue);
  });

  const CreatePONumberDisabled =
    disableActions ||
    inputError.error ||
    (!customPONumber.trim().length && generatePOBy === GeneratePOByMap[2].id);

  return (
    <Grid item xs={12} sm={12} md={12} lg={12} sx={OrderFooterSx}>
      <Button
        onClick={handleReset}
        variant="outlined"
        data-testid="reset-button"
        sx={ResetBtnSx}
      >
        Reset
      </Button>
      <FormControl
        sx={OrderFooterDropdownSx}
        size="small"
        disabled={disableActions}
      >
        <InputLabel id="generate-po-by">Generate PO By</InputLabel>
        <Select
          labelId="generate-po-by"
          id="generate-po-by"
          value={generatePOBy}
          label="Generate PO By"
          onChange={handleGeneratePOByChange}
          disabled={disableActions}
        >
          {map(GeneratePOByMap, (option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {generatePOBy === GeneratePOByMap[2].id && (
        <TextField
          required
          size="small"
          name="customPONumber"
          value={customPONumber}
          label="Custom PO Number"
          sx={OrderFooterInputSx}
          error={inputError.error}
          disabled={disableActions}
          inputProps={PONumberInputProps}
          onChange={handleCustomPONumberInput}
          helperText={inputError.error && inputError.helperText}
        />
      )}
      <Button
        variant="contained"
        sx={OrderFooterBtnSx}
        onClick={handleCreatePONumbers}
        disabled={CreatePONumberDisabled}
      >
        Create PO Numbers
      </Button>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={dateValue}
          onChange={handleDateChange}
          label="Release Date"
          renderInput={dateInputRenderer}
          OpenPickerButtonProps={DatePikerProps}
          disabled={disableActions}
        />
      </LocalizationProvider>
      <Button
        variant="contained"
        sx={OrderFooterBtnSx}
        onClick={handleSaveDates}
        disabled={!dateValue || disableActions}
      >
        Save Dates
      </Button>
      <Button
        variant="contained"
        onClick={handleDeleteDates}
        disabled={disableActions}
      >
        Delete Dates
      </Button>
    </Grid>
  );
};

export default memo(OrderFooterActions);
